import React, { useState, useEffect } from 'react';
import { Card, Typography, Button, Tag, Space, message } from 'antd';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import FeaturedLotCardSkeleton from './FeaturedLotCardSkeleton';
import { 
    StarFilled, 
    DollarOutlined,
    EyeOutlined,
    RightOutlined,
    AppstoreOutlined,
    HeartOutlined,
    HeartFilled
} from '@ant-design/icons';
import auctionService from '../../services/auctionService';
import favoriteService from '../../services/favoriteService';
import { toggleFavoriteLot } from '../../redux/userSlice';
import env from '../../env';
import moment from 'moment';
import './FeaturedLots.css';

const { Title, Text } = Typography;

const FeaturedLots = ({ limit = 10, showViewAll = true }) => {
    const [lots, setLots] = useState([]);
    const [loading, setLoading] = useState(true);
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { currentUser } = useSelector((state) => state.user);

    useEffect(() => {
        loadFeaturedLots();
    }, []);

    const loadFeaturedLots = async () => {
        try {
            setLoading(true);
            const response = await auctionService.getFeaturedLots({ limit });
            setLots(response.data || []);
        } catch (error) {
            console.error('Error loading featured lots:', error);
        } finally {
            setLoading(false);
        }
    };

    const formatCurrency = (price) => {
        if (price === null || price === undefined) return 'PKR 0.00';
        return `PKR ${Number(price).toFixed(2)}`;
    };

    const handleLotClick = (lot, e) => {
        if (e) {
            e.preventDefault();
            e.stopPropagation();
        }
        if (lot.auction?.slug && lot.slug) {
            navigate(`/auctions/${lot.auction.slug}/lots/${lot.slug}`);
        }
    };

    const getStatusColor = (status) => {
        const colors = {
            active: 'green',
            pending: 'default',
            closed: 'success',
            cancelled: 'error',
            timeout: 'warning'
        };
        return colors[status] || 'default';
    };

    const isLotFavorite = (lotId) => {
        return currentUser?.favorite_lots?.includes(lotId) || false;
    };

    const toggleFavorite = async (lotId, e) => {
        if (e) {
            e.stopPropagation();
        }
        
        if (!currentUser) {
            message.warning('Please login to add favorites');
            return;
        }

        // Update Redux state
        dispatch(toggleFavoriteLot(lotId));

        // Make the API call in the background
        try {
            await favoriteService.toggleFavoriteLot(lotId);
        } catch (error) {
            // Revert the changes on error
            dispatch(toggleFavoriteLot(lotId));
            message.error('Failed to update favorite status');
        }
    };

    const renderLotCard = (lot) => {
        const imageUrl = lot.image 
            ? (lot.image.startsWith('http') ? lot.image : `${env.REACT_APP_API_URL || env.BACKEND_STORAGE_URL}/storage/${lot.image}`)
            : null;

        return (
            <Card
                hoverable
                onClick={(e) => handleLotClick(lot, e)}
                style={{
                    borderRadius: '16px',
                    overflow: 'hidden',
                    height: '100%',
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    border: '1px solid #e8eaed',
                    boxShadow: '0 2px 12px rgba(0, 0, 0, 0.06)',
                    background: 'rgba(255, 255, 255, 0.95)',
                    backdropFilter: 'blur(10px)',
                    position: 'relative'
                }}
                cover={
                    <div style={{ position: 'relative' }}>
                        <div style={{ 
                            position: 'relative',
                            height: 160,
                            background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
                            overflow: 'hidden'
                        }}>
                                {imageUrl ? (
                                    <img
                                        alt={lot.title}
                                        src={imageUrl}
                                        style={{ 
                                            width: '100%',
                                            height: '100%',
                                            objectFit: 'cover',
                                            transition: 'transform 0.4s ease'
                                        }}
                                        onMouseOver={(e) => e.target.style.transform = 'scale(1.05)'}
                                        onMouseOut={(e) => e.target.style.transform = 'scale(1)'}
                                        onError={(e) => {
                                            e.target.style.display = 'none';
                                            if (e.target.nextSibling) {
                                                e.target.nextSibling.style.display = 'flex';
                                            }
                                        }}
                                    />
                                ) : null}
                                <div
                                    style={{
                                        width: '100%',
                                        height: '100%',
                                        display: imageUrl ? 'none' : 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        background: 'linear-gradient(135deg, #1890ff 0%, #096dd9 100%)',
                                        flexDirection: 'column',
                                        gap: '12px'
                                    }}
                                >
                                    <div style={{
                                        width: '60px',
                                        height: '60px',
                                        borderRadius: '50%',
                                        background: 'rgba(255, 255, 255, 0.2)',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        color: 'white',
                                        fontSize: '24px',
                                        fontWeight: 'bold',
                                        backdropFilter: 'blur(10px)'
                                    }}>
                                        M
                                    </div>
                                    <Text style={{ 
                                        fontSize: '12px', 
                                        textAlign: 'center',
                                        color: 'rgba(255, 255, 255, 0.9)',
                                        fontWeight: 500
                                    }}>
                                        Maveshibids
                                    </Text>
                                </div>
                                
                                {/* Featured badge */}
                                <div style={{
                                    position: 'absolute',
                                    top: 8,
                                    left: 8,
                                    zIndex: 3,
                                    background: 'linear-gradient(135deg, #ffd700 0%, #ffed4e 100%)',
                                    color: '#1f2937',
                                    padding: '2px 8px',
                                    borderRadius: '12px',
                                    fontSize: '10px',
                                    fontWeight: 700,
                                    boxShadow: '0 2px 6px rgba(255, 215, 0, 0.4)',
                                    border: '1px solid rgba(255, 255, 255, 0.3)',
                                    backdropFilter: 'blur(10px)',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '4px'
                                }}>
                                    <StarFilled style={{ fontSize: '10px' }} />
                                    Featured
                                </div>

                                {/* Favorite Button - Top Right */}
                                <Button
                                    type="text"
                                    icon={isLotFavorite(lot.id) ? <HeartFilled style={{ color: '#ff4d4f' }} /> : <HeartOutlined />}
                                    onClick={(e) => toggleFavorite(lot.id, e)}
                                    style={{
                                        position: 'absolute',
                                        top: 8,
                                        right: 8,
                                        zIndex: 3,
                                        width: '36px',
                                        height: '36px',
                                        borderRadius: '50%',
                                        background: 'rgba(255, 255, 255, 0.95)',
                                        border: 'none',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                                        padding: 0
                                    }}
                                />

                                {/* Status tag */}
                                {lot.status && (
                                    <Tag 
                                        color={getStatusColor(lot.status)} 
                                        style={{
                                            position: 'absolute',
                                            bottom: 8,
                                            right: 8,
                                            zIndex: 3,
                                            margin: 0,
                                            fontWeight: 600,
                                            fontSize: '10px',
                                            padding: '2px 8px',
                                            borderRadius: '12px',
                                            boxShadow: '0 2px 6px rgba(0, 0, 0, 0.2)'
                                        }}
                                    >
                                        {lot.status.toUpperCase()}
                                    </Tag>
                                )}
                            </div>
                        </div>
                    }
                    bodyStyle={{ padding: '16px' }}
                >
                    <Space direction="vertical" size="small" style={{ width: '100%' }}>
                        {/* Category and title */}
                        <div>
                            {lot.auction?.category && (
                                <Tag color="blue" style={{ 
                                    marginBottom: 8,
                                    borderRadius: '12px',
                                    padding: '2px 8px',
                                    fontWeight: 600,
                                    fontSize: '10px',
                                    border: 'none',
                                    background: 'linear-gradient(135deg, #1890ff 0%, #096dd9 100%)',
                                    color: 'white'
                                }}>
                                    {lot.auction.category.name}
                                </Tag>
                            )}
                            <Title level={5} style={{ 
                                margin: 0, 
                                fontSize: '16px',
                                color: '#1f2937',
                                fontWeight: 700,
                                lineHeight: 1.3,
                                marginBottom: '8px',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap'
                            }}>
                                {lot.title}
                            </Title>
                        </div>

                        {/* Lots count and price */}
                        <div style={{ 
                            display: 'flex', 
                            justifyContent: 'space-between', 
                            alignItems: 'center',
                            fontSize: '12px'
                        }}>
                            <Space size="small">
                                <AppstoreOutlined style={{ 
                                    color: '#1890ff',
                                    fontSize: '12px'
                                }} />
                                <Text style={{ 
                                    color: '#6b7280',
                                    fontSize: '11px'
                                }}>
                                    Lot #{lot.lot_number || 'N/A'}
                                </Text>
                            </Space>
                            <Space size="small">
                                <DollarOutlined style={{ color: '#52c41a', fontSize: '14px' }} />
                                <Text strong style={{ fontSize: '16px', color: '#52c41a' }}>
                                    {formatCurrency(lot.current_price || lot.starting_price)}
                                </Text>
                            </Space>
                        </div>

                        {/* View button */}
                        <Button 
                            type="primary"
                            icon={<EyeOutlined />}
                            onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                handleLotClick(lot, e);
                            }}
                            style={{
                                borderRadius: '10px',
                                fontWeight: 600,
                                height: '36px',
                                padding: '0 16px',
                                background: 'linear-gradient(135deg, #1890ff 0%, #096dd9 100%)',
                                border: 'none',
                                boxShadow: '0 2px 8px rgba(24, 144, 255, 0.3)',
                                transition: 'all 0.3s ease',
                                fontSize: '12px'
                            }}
                            size="small"
                        >
                            {(() => {
                                const now = moment();
                                const auctionStartTime = lot.auction?.start_time ? moment(lot.auction.start_time) : null;
                                const isToday = auctionStartTime && auctionStartTime.isSame(moment(), 'day');
                                
                                if (lot.auction?.status === 'completed') {
                                    return 'View Lot';
                                } else if (isToday && lot.auction?.status === 'active') {
                                    return 'Enter Auction';
                                } else if (auctionStartTime && auctionStartTime.isAfter(now)) {
                                    return 'View Lot';
                                } else {
                                    return 'View Lot';
                                }
                            })()}
                        </Button>
                    </Space>
                </Card>
            );
    };

    if (loading) {
        return (
            <div style={{ marginBottom: 40 }}>
                {/* Header */}
                <div style={{ 
                    marginBottom: 24,
                    padding: '24px',
                    background: 'linear-gradient(135deg, #ffd700 0%, #ffed4e 100%)',
                    borderRadius: '16px',
                    boxShadow: '0 8px 24px rgba(255, 215, 0, 0.2)',
                    position: 'relative',
                    overflow: 'hidden'
                }}>
                    {/* Background decoration */}
                    <div style={{
                        position: 'absolute',
                        top: '-50%',
                        right: '-50%',
                        width: '200%',
                        height: '200%',
                        background: 'radial-gradient(circle, rgba(255, 215, 0, 0.1) 0%, transparent 70%)',
                        animation: 'float 6s ease-in-out infinite'
                    }} />
                    
                    <div style={{ position: 'relative', zIndex: 2 }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
                                <StarFilled style={{ 
                                    fontSize: '24px', 
                                    color: '#1f2937' 
                                }} />
                                <Title level={3} style={{ 
                                    margin: 0, 
                                    color: '#1f2937',
                                    fontWeight: 800,
                                    fontSize: '24px'
                                }}>
                                    Featured Lots
                                </Title>
                            </div>
                            {showViewAll && (
                                <Button 
                                    type="primary"
                                    icon={<RightOutlined />}
                                    onClick={() => navigate('/featured-lots')}
                                    style={{
                                        fontSize: '14px',
                                        fontWeight: 600,
                                        height: '40px',
                                        padding: '0 20px',
                                        borderRadius: '10px',
                                        background: 'linear-gradient(135deg, #1f2937 0%, #374151 100%)',
                                        border: 'none',
                                        boxShadow: '0 4px 12px rgba(31, 41, 55, 0.3)',
                                        transition: 'all 0.3s ease'
                                    }}
                                    onMouseEnter={(e) => {
                                        e.currentTarget.style.transform = 'translateY(-2px)';
                                        e.currentTarget.style.boxShadow = '0 6px 16px rgba(31, 41, 55, 0.4)';
                                    }}
                                    onMouseLeave={(e) => {
                                        e.currentTarget.style.transform = 'translateY(0)';
                                        e.currentTarget.style.boxShadow = '0 4px 12px rgba(31, 41, 55, 0.3)';
                                    }}
                                >
                                    View all Featured Lots
                                </Button>
                            )}
                        </div>
                        <Text style={{ 
                            fontSize: '16px',
                            color: '#374151',
                            fontWeight: 500,
                            lineHeight: 1.5
                        }}>
                            Discover our handpicked selection of premium lots
                        </Text>
                    </div>
                </div>

                {/* Horizontal scrollable row */}
                <div
                    className="featured-lots-scroll"
                    style={{
                        display: 'flex',
                        overflowX: 'auto',
                        gap: 16,
                        paddingBottom: 8,
                        scrollbarWidth: 'thin',
                    }}
                >
                    {Array.from({ length: 10 }).map((_, idx) => (
                        <div key={idx} style={{ flex: '0 0 240px', minWidth: 240 }}>
                            <FeaturedLotCardSkeleton />
                        </div>
                    ))}
                </div>

                <style jsx>{`
                    @keyframes float {
                        0%, 100% { transform: translateY(0px) rotate(0deg); }
                        50% { transform: translateY(-10px) rotate(180deg); }
                    }
                `}</style>
            </div>
        );
    }

    if (lots.length === 0) {
        return null;
    }

    return (
        <div style={{ marginBottom: 40 }}>
            {/* Header */}
            <div style={{ 
                marginBottom: 24,
                padding: '24px',
                background: 'linear-gradient(135deg, #ffd700 0%, #ffed4e 100%)',
                borderRadius: '16px',
                boxShadow: '0 8px 24px rgba(255, 215, 0, 0.2)',
                position: 'relative',
                overflow: 'hidden'
            }}>
                {/* Background decoration */}
                <div style={{
                    position: 'absolute',
                    top: '-50%',
                    right: '-50%',
                    width: '200%',
                    height: '200%',
                    background: 'radial-gradient(circle, rgba(255, 215, 0, 0.1) 0%, transparent 70%)',
                    animation: 'float 6s ease-in-out infinite'
                }} />
                
                <div style={{ position: 'relative', zIndex: 2 }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
                            <StarFilled style={{ 
                                fontSize: '24px', 
                                color: '#1f2937' 
                            }} />
                            <Title level={3} style={{ 
                                margin: 0, 
                                color: '#1f2937',
                                fontWeight: 800,
                                fontSize: '24px'
                            }}>
                                Featured Lots
                            </Title>
                        </div>
                        {showViewAll && (
                            <Button 
                                type="primary"
                                icon={<RightOutlined />}
                                onClick={() => navigate('/featured-lots')}
                                style={{
                                    fontSize: '14px',
                                    fontWeight: 600,
                                    height: '40px',
                                    padding: '0 20px',
                                    borderRadius: '10px',
                                    background: 'linear-gradient(135deg, #1f2937 0%, #374151 100%)',
                                    border: 'none',
                                    boxShadow: '0 4px 12px rgba(31, 41, 55, 0.3)',
                                    transition: 'all 0.3s ease'
                                }}
                                onMouseEnter={(e) => {
                                    e.currentTarget.style.transform = 'translateY(-2px)';
                                    e.currentTarget.style.boxShadow = '0 6px 16px rgba(31, 41, 55, 0.4)';
                                }}
                                onMouseLeave={(e) => {
                                    e.currentTarget.style.transform = 'translateY(0)';
                                    e.currentTarget.style.boxShadow = '0 4px 12px rgba(31, 41, 55, 0.3)';
                                }}
                            >
                                View all Featured Lots
                            </Button>
                        )}
                    </div>
                    <Text style={{ 
                        fontSize: '16px',
                        color: '#374151',
                        fontWeight: 500,
                        lineHeight: 1.5
                    }}>
                        Discover our handpicked selection of premium lots
                    </Text>
                </div>
            </div>

            {/* Horizontal scrollable row */}
            <div
                className="featured-lots-scroll"
                style={{
                    display: 'flex',
                    overflowX: 'auto',
                    gap: 16,
                    paddingBottom: 8,
                    scrollbarWidth: 'thin',
                }}
            >
                {lots.map((lot) => (
                    <div key={lot.id} style={{ flex: '0 0 240px', minWidth: 240 }}>
                        {renderLotCard(lot)}
                    </div>
                ))}
            </div>

            <style jsx>{`
                @keyframes float {
                    0%, 100% { transform: translateY(0px) rotate(0deg); }
                    50% { transform: translateY(-10px) rotate(180deg); }
                }
            `}</style>
        </div>
    );
};

export default FeaturedLots;

